import { useState, useRef, useEffect, lazy, Suspense } from "react";
import {
  ChevronDown, Phone, MapPin, ArrowRight,
  Zap, Home, Map, CheckCircle, Loader2,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import FoerderRechner from "../components/foerder-rechner/FoerderRechner";
import ROISimulator from "../components/roi-simulator/ROISimulator";
import LoadingSpinner from "../components/shared/LoadingSpinner";
import NEKOLogo from "../components/shared/NEKOLogo";
import { track } from "../lib/tracking";
import { api } from "../lib/api";
import { readMeta } from "../lib/tenant-branding";

const SolarRoofMapper = lazy(() => import("../components/dach-mapper/SolarRoofMapper"));

type ActiveTool = "foerder" | "dach-mapper" | "solar";

/** Tenant-aware logo: renders <img> when tenant has logo_url, else falls back to NEKOLogo. */
function TenantLogo({ size }: { size: "sm" | "md" | "lg" }) {
  const logoUrl = readMeta("x-tenant-logo-url");
  const tenantName = readMeta("x-tenant-name");
  if (!logoUrl) return <NEKOLogo size={size} />;
  const heightClass = size === "sm" ? "h-8" : size === "md" ? "h-12" : "h-20 md:h-28";
  return (
    <img
      src={logoUrl}
      alt={tenantName || "Logo"}
      loading="lazy"
      className={`${heightClass} w-auto object-contain`}
    />
  );
}

const STEPS = [
  {
    nr: "01",
    title: "Rechner ausfüllen",
    desc: "In 2 Minuten individuell für Ihr Gebäude berechnen — ohne Anmeldung.",
  },
  {
    nr: "02",
    title: "PDF & Beratung erhalten",
    desc: "Kostenloses PDF mit Ihren Ergebnissen, wir melden uns innerhalb von 24 Stunden.",
  },
  {
    nr: "03",
    title: "Wir übernehmen alles",
    desc: "Planung, Installation, Förderantrag — alles aus einer Hand, ohne Koordinationsaufwand.",
  },
];

export default function LandingPage() {
  const [activeTool, setActiveTool] = useState<ActiveTool>("foerder");
  const [scrolled, setScrolled] = useState(false);
  const toolsRef = useRef<HTMLElement>(null);

  // Tenant-aware fields injected by middleware via meta tags. Empty string when
  // the tenant didn't supply a value — UI elements gate on truthiness and hide.
  const tenantPhone = readMeta("x-tenant-phone");
  const tenantImpressumUrl = readMeta("x-tenant-impressum-url") || "https://www.neko-bws.de/impressum";
  const tenantDatenschutzUrl = readMeta("x-tenant-datenschutz-url") || "https://www.neko-bws.de/datenschutz";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  function scrollToTools(tool: ActiveTool) {
    setActiveTool(tool);
    track("cta_click", { tool });
    setTimeout(() => {
      toolsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }

  function handleToolTab(tool: ActiveTool) {
    setActiveTool(tool);
    track("tool_engagement", { tool });
  }

  return (
    <div className="min-h-screen bg-neko-dark text-neko-white font-raleway">

      {/* ── NAV ───────────────────────────────────────────────────── */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-neko-dark/90 backdrop-blur-md border-b border-white/[0.06]" : ""
        }`}
      >
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <TenantLogo size="sm" />
          {tenantPhone && (
            <a
              href={`tel:${tenantPhone.replace(/\s/g, "")}`}
              onClick={() => track("phone_click", { location: "nav" })}
              className="flex items-center gap-2 text-xs font-[300] tracking-widest uppercase text-white/40 hover:text-neko-gold transition-colors duration-300"
            >
              <Phone className="w-3.5 h-3.5" />
              {tenantPhone}
            </a>
          )}
        </div>
      </nav>

      {/* ── HERO ──────────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex flex-col items-center justify-center px-6 pt-24 pb-20 overflow-hidden">
        <div
          className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(ellipse at center, rgba(240,168,48,0.05) 0%, transparent 70%)" }}
        />

        <motion.div
          className="max-w-2xl mx-auto text-center relative z-10"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="flex justify-center mb-14">
            <TenantLogo size="lg" />
          </div>

          <div className="w-12 h-px bg-neko-gold/40 mx-auto mb-10" />

          <h1 className="font-[200] text-3xl md:text-4xl tracking-[0.06em] leading-relaxed text-white/90 mb-5">
            Smarte Energie für Ihr Zuhause
          </h1>
          <p className="font-[300] text-sm md:text-base tracking-wider text-white/40 leading-loose max-w-lg mx-auto mb-14">
            Berechnen Sie Ihre Förderung und Ihren Solar-Ertrag —<br className="hidden md:block" />
            kostenlos, unverbindlich, in wenigen Minuten.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
            <button
              onClick={() => scrollToTools("foerder")}
              className="group flex items-center justify-center gap-3 px-8 py-4 border border-neko-gold/70 text-neko-gold hover:bg-neko-gold hover:text-neko-dark transition-all duration-300 text-[11px] tracking-[0.2em] uppercase font-[300]"
            >
              <Home className="w-3.5 h-3.5" />
              Förderung berechnen
              <ArrowRight className="w-3.5 h-3.5 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
            </button>
            <button
              onClick={() => scrollToTools("dach-mapper")}
              className="group flex items-center justify-center gap-3 px-8 py-4 border border-white/15 text-white/60 hover:border-neko-gold/50 hover:text-neko-gold transition-all duration-300 text-[11px] tracking-[0.2em] uppercase font-[300]"
            >
              <Map className="w-3.5 h-3.5" />
              Dach-Potential prüfen
              <ArrowRight className="w-3.5 h-3.5 opacity-40 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
            </button>
            <button
              onClick={() => scrollToTools("solar")}
              className="group flex items-center justify-center gap-3 px-8 py-4 border border-white/15 text-white/60 hover:border-neko-gold/50 hover:text-neko-gold transition-all duration-300 text-[11px] tracking-[0.2em] uppercase font-[300]"
            >
              <Zap className="w-3.5 h-3.5" />
              Solar-Ertrag berechnen
              <ArrowRight className="w-3.5 h-3.5 opacity-40 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
            </button>
          </div>
        </motion.div>

        <motion.div
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <ChevronDown className="w-4 h-4 text-white/20" />
        </motion.div>
      </section>

      {/* ── SO EINFACH GEHT'S ─────────────────────────────────────── */}
      <section className="py-20 px-6">
        <motion.div
          className="max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="text-center mb-12">
            <div className="w-8 h-px bg-neko-gold/40 mx-auto mb-8" />
            <h2 className="font-[200] text-2xl md:text-3xl tracking-[0.08em] text-white/90">
              So einfach geht's
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-white/[0.04]">
            {STEPS.map((s) => (
              <div key={s.nr} className="bg-neko-dark px-8 py-10">
                <div
                  className="font-[100] text-4xl tracking-widest mb-6"
                  style={{ color: "rgba(240,168,48,0.3)" }}
                >
                  {s.nr}
                </div>
                <h3 className="font-[300] text-sm tracking-[0.15em] uppercase text-white/80 mb-3">
                  {s.title}
                </h3>
                <p className="font-[300] text-sm text-white/35 leading-loose tracking-wide">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ── TOOLS ─────────────────────────────────────────────────── */}
      <section ref={toolsRef} id="rechner" className="py-20 px-6 border-t border-white/[0.06]">
        <div className={`mx-auto ${activeTool === "dach-mapper" ? "max-w-4xl" : "max-w-2xl"} transition-all duration-300`}>
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="w-8 h-px bg-neko-gold/40 mx-auto mb-8" />
            <h2 className="font-[200] text-2xl md:text-3xl tracking-[0.08em] text-white/90 mb-4">
              Jetzt kostenlos berechnen
            </h2>
            <p className="font-[300] text-sm tracking-wider text-white/35 leading-loose">
              Individuell für Ihr Gebäude — kostenlos und unverbindlich
            </p>
          </motion.div>

          <div className="flex border-b border-white/[0.08] mb-10">
            {([
              { id: "foerder" as const, label: "Förder-Rechner", icon: Home },
              { id: "dach-mapper" as const, label: "Dach-Mapper", icon: Map },
              { id: "solar" as const, label: "Solar-Rechner", icon: Zap },
            ]).map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => handleToolTab(id)}
                className={`flex items-center gap-2 px-6 py-3.5 text-[11px] tracking-[0.2em] uppercase font-[300] border-b-[1.5px] -mb-px transition-all duration-300 ${
                  activeTool === id
                    ? "border-neko-gold text-neko-gold"
                    : "border-transparent text-white/30 hover:text-white/60"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeTool}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
            >
              {activeTool === "foerder" && <FoerderRechner onToolChange={(tool) => {
                const map: Record<string, ActiveTool> = { "dach-mapper": "dach-mapper", "roi-simulator": "solar", "foerder-rechner": "foerder" };
                handleToolTab(map[tool] ?? "foerder");
              }} />}
              {activeTool === "dach-mapper" && (
                <Suspense fallback={<LoadingSpinner text="Dach-Mapper wird geladen…" />}>
                  <SolarRoofMapper onToolChange={(tool) => {
                    const map: Record<string, ActiveTool> = { "roi-simulator": "solar", "foerder-rechner": "foerder", "dach-mapper": "dach-mapper" };
                    handleToolTab(map[tool] ?? "solar");
                  }} />
                </Suspense>
              )}
              {activeTool === "solar" && <ROISimulator />}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ── DIREKTFORMULAR ────────────────────────────────────────── */}
      <DirectContactForm />

      {/* ── FOOTER ────────────────────────────────────────────────── */}
      <footer className="border-t border-white/[0.06] py-8 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-5">
          <TenantLogo size="sm" />
          <div className="flex gap-8 text-[10px] font-[300] tracking-[0.22em] uppercase text-white/25">
            <a href={tenantImpressumUrl} target="_blank" rel="noopener noreferrer" className="hover:text-white/50 transition-colors">Impressum</a>
            <a href={tenantDatenschutzUrl} target="_blank" rel="noopener noreferrer" className="hover:text-white/50 transition-colors">Datenschutz</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ── Direktformular Komponente ────────────────────────────────────────────────
function DirectContactForm() {
  const [name, setName] = useState("");
  const [telefon, setTelefon] = useState("");
  const [interesse, setInteresse] = useState("pv");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tenantName = readMeta("x-tenant-name");
  const tenantPhone = readMeta("x-tenant-phone");
  const tenantAddress = readMeta("x-tenant-address");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !telefon.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await api.directContact.submit({ name: name.trim(), telefon: telefon.trim(), interesse });
      setSuccess(true);
      track("generate_lead", { method: "direct_contact", interesse });
    } catch {
      setError("Fehler beim Senden. Bitte rufen Sie uns direkt an.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="py-20 px-6 border-t border-white/[0.06]">
      <motion.div
        className="max-w-lg mx-auto"
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="text-center mb-10">
          <div className="w-8 h-px bg-neko-gold/40 mx-auto mb-8" />
          <h2 className="font-[200] text-2xl tracking-[0.08em] text-white/90 mb-3">
            Lieber direkt angerufen werden?
          </h2>
          <p className="font-[300] text-sm tracking-wider text-white/35 leading-loose">
            Hinterlassen Sie Ihre Nummer — wir melden uns zeitnah.
          </p>
        </div>

        {success ? (
          <div className="text-center py-10">
            <CheckCircle className="w-8 h-8 mx-auto mb-4" style={{ color: "#f0a830" }} />
            <p className="font-[300] text-sm tracking-wider text-white/60">
              Danke! Wir melden uns zeitnah bei Ihnen.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[10px] tracking-[0.25em] uppercase text-white/30 font-[300] mb-2">
                Name
              </label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Max Mustermann"
                className="w-full bg-transparent border border-white/10 px-4 py-3.5 text-sm font-[300] text-white/80 placeholder-white/20 focus:outline-none focus:border-neko-gold/50 transition-colors tracking-wide"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.25em] uppercase text-white/30 font-[300] mb-2">
                Telefonnummer
              </label>
              <input
                type="tel"
                required
                value={telefon}
                onChange={(e) => setTelefon(e.target.value)}
                placeholder="+49 173 1234567"
                className="w-full bg-transparent border border-white/10 px-4 py-3.5 text-sm font-[300] text-white/80 placeholder-white/20 focus:outline-none focus:border-neko-gold/50 transition-colors tracking-wide"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.25em] uppercase text-white/30 font-[300] mb-2">
                Interesse
              </label>
              <select
                value={interesse}
                onChange={(e) => setInteresse(e.target.value)}
                className="w-full bg-neko-dark border border-white/10 px-4 py-3.5 text-sm font-[300] text-white/80 focus:outline-none focus:border-neko-gold/50 transition-colors tracking-wide"
              >
                <option value="pv">Photovoltaik / Solar</option>
                <option value="waermepumpe">Wärmepumpe / Heizung</option>
                <option value="beides">PV + Wärmepumpe</option>
                <option value="sonstiges">Elektro / Sanitär / Sonstiges</option>
              </select>
            </div>

            {error && (
              <p className="text-xs text-neko-red tracking-wide font-[300]">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 border border-neko-gold/60 text-neko-gold hover:bg-neko-gold hover:text-neko-dark transition-all duration-300 text-[11px] tracking-[0.2em] uppercase font-[300] flex items-center justify-center gap-2 disabled:opacity-40"
            >
              {loading ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Wird gesendet…</>
              ) : (
                <>Rückruf anfragen</>
              )}
            </button>

            <p className="text-center text-[10px] tracking-widest uppercase text-white/20 font-[300]">
              Kein Spam {tenantName ? `· Nur ${tenantName} kontaktiert Sie` : ""}
            </p>
          </form>
        )}

        {(tenantPhone || tenantAddress) && (
          <div className="mt-10 pt-8 border-t border-white/[0.06] flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 text-xs font-[300] tracking-widest text-white/25">
            {tenantPhone && (
              <a
                href={`tel:${tenantPhone.replace(/\s/g, "")}`}
                className="flex items-center gap-2 hover:text-neko-gold transition-colors"
              >
                <Phone className="w-3.5 h-3.5" />
                {tenantPhone}
              </a>
            )}
            {tenantPhone && tenantAddress && <span className="hidden sm:inline">·</span>}
            {tenantAddress && (
              <span className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                {tenantAddress}
              </span>
            )}
          </div>
        )}
      </motion.div>
    </section>
  );
}
